import { Link, TableCell, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  createSearchRequestProvider,
  getActiveFiltersCount,
  preprocessSearchRequest,
  withoutBaseTableModel,
} from "~/components/Table";
import {
  commonResourceColumns,
  selectCountableListResponse,
} from "~/domain/common";
import type { APIKey, LqsColumn } from "~/lqs";
import { LqsResourceTable, useApiKeys } from "~/lqs";
import { useLqsNavigator } from "~/paths";
import { listApiKeysSchema } from "../schemas";

function ApiKeyNameCell({ apiKey }: { apiKey: APIKey }) {
  const lqsNavigator = useLqsNavigator({ toApiKeyDetails: true });

  return (
    <TableCell>
      {apiKey.name}
      <Link
        component={RouterLink}
        to={lqsNavigator.toApiKeyDetails({ apiKeyId: apiKey.id })}
      >
        <Typography variant="body2">{apiKey.id}</Typography>
      </Link>
    </TableCell>
  );
}

const columns: ReadonlyArray<LqsColumn<"apiKey">> = [
  {
    header: "Name",
    sortKey: "name",
    renderCell: (apiKey) => <ApiKeyNameCell apiKey={apiKey} />,
  },
  {
    accessor: "userId",
    dataType: "foreign-key",
    resourceType: "user",
  },
  {
    accessor: "disabled",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  ...commonResourceColumns,
];

export const [useApiKeySearchRequest, ApiKeySearchRequestProvider] =
  createSearchRequestProvider({
    columns,
    filterSchema: listApiKeysSchema,
  });

export function ApiKeyTable() {
  const [request, setRequest] = useApiKeySearchRequest();

  const lqsNavigator = useLqsNavigator({ toApiKeyCreate: true });

  const searchQuery = useApiKeys(preprocessSearchRequest(request), {
    keepPreviousData: true,
    cacheTime: 0,
    select: selectCountableListResponse,
  });

  const filterValues = withoutBaseTableModel(request);

  return (
    <LqsResourceTable
      resourceName="API Key"
      registryKey="apiKey"
      resourceCreateLocation={lqsNavigator.toApiKeyCreate()}
      getRowKey={(apiKey) => apiKey.id}
      columns={columns}
      searchQuery={searchQuery}
      tableModel={request}
      onTableModelChange={setRequest}
      activeFilterCount={getActiveFiltersCount(filterValues)}
    />
  );
}
